import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Button, Input } from "@heroui/react";
import { DeviceType } from "@/types/common";
import { ColorPicker } from "@/shared/components/common/ColorPicker";
import { FOLDER_COLORS } from "../constants";
import { FolderColor } from "../types";

export interface FolderFormValues {
  name: string;
  color: FolderColor | null;
}

interface FolderFormContentProps {
  initialValues?: FolderFormValues;
  onSubmit: (values: FolderFormValues) => void;
  onClose?: () => void;
  deviceType: DeviceType;
}

export const FolderFormContent = forwardRef<{ submit: () => void }, FolderFormContentProps>(
  ({ initialValues, onSubmit, onClose, deviceType }, ref) => {
    const { formValues, handleChange, handleColorChange, resetForm, errors, validateForm } =
      useFolderForm(initialValues);

    const handleSubmit = () => {
      if (validateForm()) {
        onSubmit(formValues);
        onClose?.();
        resetForm();
      }
    };

    useImperativeHandle(ref, () => ({
      submit: handleSubmit,
      reset: resetForm,
    }));

    const handleNameKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && formValues.name.trim()) {
        e.preventDefault();
        handleSubmit();
      } else if (e.key === "Escape") {
        resetForm();
        onClose?.();
      }
    };

    return (
      <div className="space-y-4">
        <Input
          autoFocus={!initialValues}
          autoComplete="off"
          errorMessage={errors.name}
          isInvalid={!!errors.name}
          name="name"
          label="Folder name"
          placeholder="Enter folder name"
          size="sm"
          value={formValues.name}
          onChange={handleChange}
          onKeyDown={handleNameKeyDown}
        />

        <ColorPicker
          colors={FOLDER_COLORS}
          selectedColor={formValues.color}
          onChange={handleColorChange}
          label="Select color"
        />
        {deviceType === "mobile" && (
          <Button color="primary" fullWidth onPress={handleSubmit}>
            {initialValues ? "Save" : "Create"}
          </Button>
        )}
      </div>
    );
  }
);

function useFolderForm(initialValues?: FolderFormValues) {
  const [formValues, setFormValues] = useState<FolderFormValues>({
    name: initialValues?.name || "",
    color: initialValues?.color ?? null,
  });
  const [errors, setErrors] = useState<{ name?: string }>({});

  useEffect(() => {
    if (initialValues) {
      setFormValues({
        name: initialValues.name || "",
        color: initialValues.color ?? null,
      });
    } else {
      setFormValues({
        name: "",
        color: null,
      });
    }
  }, [initialValues]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleColorChange = (color: FolderColor | null) => {
    setFormValues((prev) => ({ ...prev, color }));
  };

  const validateForm = () => {
    const newErrors: { name?: string } = {};

    if (!formValues.name.trim()) {
      newErrors.name = "Folder name is required.";
    } else if (formValues.name.trim().length > 100) {
      newErrors.name = "Folder name must not exceed 100 characters.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormValues({
      name: initialValues?.name || "",
      color: initialValues?.color ?? null,
    });
    setErrors({});
  };

  return {
    formValues,
    handleChange,
    handleColorChange,
    resetForm,
    errors,
    validateForm,
  };
}
